import {
  BrowserDetection,
  ConfigService,
  RunConfigService
} from "@nuskin/ns-util";
import { ProductData, Product } from "@nuskin/ns-product-lib";
import { getConfiguration } from "@nuskin/configuration-sdk";

/**
 * Used to retrieve product content and product status for products requested from the product data lambda.
 * @param skus The product skus to request data for.
 * @param locale The locale for market-specific product data and translations.
 * @return {Promise<*>}
 */
async function getProductData(skus, locale) {
  let runConfig = RunConfigService.getRunConfig();
  if (locale) {
    const [language, country] = locale.split("_"); // -> [en, US]
    runConfig = { language, country };
  }
  const marketConfig = ConfigService.getMarketConfig();
  const equinoxMarketConfig = (await getConfiguration(["Equinox_Markets"]))
    .Equinox_Markets;
  const isEquinoxEnabled = equinoxMarketConfig.active;
  const useGraphQl = equinoxMarketConfig.MySite_graphql_active;
  if (typeof skus === "string") {
    const newSkus = new Array();
    newSkus.push(skus);
    skus = newSkus;
  }

  if (!useGraphQl) {
    const products = await ProductData.getProductData(
      skus,
      runConfig.language,
      runConfig.country,
      isEquinoxEnabled
    );
    const productData = processProductDataResponse(marketConfig, products);
    return productData;
  }

  const products = await Promise.all(
    skus.map(async (sku) => {
      const skuToGet = new Array();
      skuToGet.push(sku);
      const prod = await ProductData.getProductData(
        skuToGet,
        runConfig.language,
        runConfig.country,
        isEquinoxEnabled
      );
      return prod;
    })
  );
  const productsMapped = new Array();
  if (isEquinoxEnabled) {
    const pMapped = products.map(
      (p) => p.data.data.products.filter((p) => p !== null && p.sku !== "")[0]
    );
    productsMapped.push({
      data: {
        status: "200",
        products: pMapped,
        count: pMapped.length
      }
    });
  } else {
    productsMapped.push(products);
  }

  const pReturn = processProductDataResponse(marketConfig, productsMapped[0]);

  return pReturn;
}

function processProductDataResponse(marketConfig, productDataResponse) {
  if (!productDataResponse || !productDataResponse.data) {
    return undefined;
  }

  let productData = productDataResponse.data;
  if (productData.data) {
    // the actual response is nested in another data for now
    productData = productData.data;
  }

  if (productData.products && productData.products.length) {
    const siteUrl = marketConfig.siteUrl;
    const isEdge = BrowserDetection.isEdge();
    const isFirefox = BrowserDetection.isFirefox();

    productData.products = productData.products.map((pdata) =>
      mapProductFromResponse(siteUrl, isEdge, isFirefox, pdata)
    );
  }

  return productData;
}

function mapProductFromResponse(siteUrl, isEdge, isFirefox, pdata) {
  let product;
  if (pdata instanceof Product) {
    product = new Product(pdata.toJSON());
  } else {
    product = new Product(pdata);
  }

  // set the base url for images, etc
  product.setBaseUrl(siteUrl);

  // fix image paths for non-edge/firefox
  if (!isEdge && !isFirefox) {
    product.fullImage = product.fullImage
      ? `${product.fullImage.split("?")[0]}?format=pjpg`
      : undefined;
    product.thumbnail = product.thumbnail
      ? `${product.thumbnail.split("?")[0]}?format=pjpg`
      : undefined;
  }

  return product;
}

export default {
  getProductData
};
